const toHexByte = (channelValue: number): string => channelValue.toString(16).padStart(2, '0');

// Parses a #-prefixed hex color into RGB channels, expanding 3/4-digit shorthand
// and rejecting anything that isn't a well-formed 6- or 8-digit hex (returns null
// so callers can fall back to the original string rather than emit garbage).
const parseHexColor = (color: string): [number, number, number] | null => {
  let hex = color.slice(1);
  if (hex.length === 3 || hex.length === 4) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('');
  }
  if (hex.length !== 6 && hex.length !== 8) return null;
  const red = Number.parseInt(hex.substring(0, 2), 16);
  const green = Number.parseInt(hex.substring(2, 4), 16);
  const blue = Number.parseInt(hex.substring(4, 6), 16);
  if (Number.isNaN(red) || Number.isNaN(green) || Number.isNaN(blue)) return null;
  return [red, green, blue];
};

// Parses a hex or space-separated rgb()/rgba() color, applies `transformChannel`
// to each RGB channel, and re-serializes in the same format (hex in, hex out;
// rgb in, rgb out with alpha preserved). Malformed hex and legacy comma-separated
// rgb are not recognized and return the original string untouched.
const adjustTimelineBgColor = (
  color: string,
  transformChannel: (channelValue: number) => number,
): string => {
  if (color.startsWith('#')) {
    const parts = parseHexColor(color);
    if (!parts) return color;
    const [red, green, blue] = parts;
    return `#${toHexByte(transformChannel(red))}${toHexByte(transformChannel(green))}${toHexByte(transformChannel(blue))}`;
  }

  const match = color.match(/rgba?\((\d+)\s+(\d+)\s+(\d+)(?:\s*\/\s*([\d.]+))?\)/);
  if (!match?.[1] || !match[2] || !match[3]) {
    return color;
  }
  const red = transformChannel(Number.parseInt(match[1], 10));
  const green = transformChannel(Number.parseInt(match[2], 10));
  const blue = transformChannel(Number.parseInt(match[3], 10));
  const alpha = match[4];
  return alpha ? `rgb(${red} ${green} ${blue} / ${alpha})` : `rgb(${red} ${green} ${blue})`;
};

// Callers can derive percent from unbounded inputs (e.g. depth * step), so clamp
// to [0, 100]. Beyond that the factor would push channels below 0 or above 255,
// producing out-of-range bytes and invalid hex/rgb output.
const clampPercent = (percent: number): number => Math.min(100, Math.max(0, percent));

export const darkenTimelineBgColor = (color: string, percent: number = 20): string => {
  const factor = 1 - clampPercent(percent) / 100;
  return adjustTimelineBgColor(color, (channelValue) => Math.round(channelValue * factor));
};

export const lightenTimelineBgColor = (color: string, percent: number = 20): string => {
  const factor = clampPercent(percent) / 100;
  return adjustTimelineBgColor(color, (channelValue) =>
    Math.round(channelValue + (255 - channelValue) * factor),
  );
};

export const getProgressUnfilledColor = (color: string): string =>
  lightenTimelineBgColor(color, 30);

const toRgbParts = (color: string): [number, number, number] | null => {
  if (color.startsWith('#')) {
    return parseHexColor(color);
  }
  const match = color.match(/rgba?\((\d+)\s*[,\s]\s*(\d+)\s*[,\s]\s*(\d+)/);
  if (!match?.[1] || !match[2] || !match[3]) return null;
  return [
    Number.parseInt(match[1], 10),
    Number.parseInt(match[2], 10),
    Number.parseInt(match[3], 10),
  ];
};

// Translucent tint of the item color so it reads over any surface (light or dark),
// replacing the previous lighten-toward-white blend that broke in dark mode.
export const timelineBgColorWithAlpha = (color: string, alpha: number): string => {
  const parts = toRgbParts(color);
  if (!parts) return color;
  const [r, g, b] = parts;
  const clampedAlpha = Math.min(1, Math.max(0, alpha));
  return `rgb(${r} ${g} ${b} / ${clampedAlpha})`;
};
